import type { MouseEvent } from 'react'

/**
 * Elements that own their click inside a table row: links (e.g. the user-detail link in a
 * buyer/dealer cell), buttons (the "view" action, dropdown triggers), form controls and antd
 * widgets rendered as plain elements.
 */
const NESTED_INTERACTIVE_SELECTOR = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  'label',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="option"]',
  '.ant-table-row-expand-icon',
  '.ant-checkbox-wrapper',
  '.ant-select',
  '.ant-image',
].join(',')

/**
 * Whether a click reaching a row's `onClick` was meant for something else: an interactive
 * element nested in the row, or a portal (Modal, Popconfirm, Dropdown) whose React events
 * bubble through the row even though its DOM lives outside it.
 */
export function isNestedInteractiveClick(event: MouseEvent<HTMLElement>): boolean {
  const { target, currentTarget } = event
  if (!(target instanceof Element)) return false
  if (!currentTarget.contains(target)) return true
  const interactive = target.closest(NESTED_INTERACTIVE_SELECTOR)
  return (
    interactive !== null && interactive !== currentTarget && currentTarget.contains(interactive)
  )
}

/**
 * Row `onClick` for antd `Table onRow` that navigates only when the row itself was clicked, so a
 * user link or action button inside the row does not also open the row's detail page.
 */
export function createRowClickHandler(
  onRowClick: () => void,
): (event: MouseEvent<HTMLElement>) => void {
  return (event) => {
    if (isNestedInteractiveClick(event)) return
    onRowClick()
  }
}
